import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../services/api";
import TimePartsInput from "../components/TimePartsInput";
import "./TimeOverForm.css";

/** 添付画像の最大サイズ（5MB） */
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
/** 受け付ける拡張子 */
const ACCEPT_ATTACHMENT = "image/*,application/pdf";

const CATEGORY_CONFIG = {
  OVERTIME: {
    label: "残業申請",
    color: "#6366f1",
    hint: "定時後に残業を行った場合、または予定している場合に申請してください。",
    hintBg: "#f0f0ff",
    hintColor: "#4338ca",
  },
  LATE_IN: {
    label: "遅刻申請",
    color: "#f59e0b",
    hint: "始業時刻より遅れて出社する場合に申請してください。",
    hintBg: "#fffbeb",
    hintColor: "#92400e",
  },
  EARLY_OUT: {
    label: "早退申請",
    color: "#10b981",
    hint: "終業時刻より早く退社する場合に申請してください。",
    hintBg: "#f0fdf4",
    hintColor: "#065f46",
  },
};

const STATUS_LABELS = {
  SUBMITTED: { label: "申請中", bg: "#6366f1" },
  APPROVED: { label: "承認済", bg: "#16a34a" },
  REJECTED: { label: "却下", bg: "#dc2626" },
  RETURNED: { label: "差戻し", bg: "#f59e0b" },
};

// 残業休憩時間の選択肢（時間単位 0〜6.0 を 0.25 刻み）。内部は分で保持する。
const OVERTIME_BREAK_HOUR_OPTIONS = Array.from({ length: 25 }, (_, i) => i * 0.25);

const calcMinutes = (start, end) => {
  if (!start || !end) return null;
  try {
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    return (eh * 60 + em) - (sh * 60 + sm);
  } catch {
    return null;
  }
};

const formatMinutes = (mins) => {
  if (mins === null || mins === undefined || mins < 0) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}時間${m > 0 ? m + "分" : ""}`;
};

const today = () => new Date().toISOString().slice(0, 10);

/** 現在時刻を HH:MM で返す */
const nowHm = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

/** "18:00:00" / "18:00" いずれも "18:00" に揃える */
const toHm = (v) => (v ? String(v).slice(0, 5) : "");

const TimeOverForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState("apply");
  const [category, setCategory] = useState("OVERTIME");
  const [date, setDate] = useState(today());
  const [overtimeHours, setOvertimeHours] = useState("");
  const [breakTime, setBreakTime] = useState("00:00");
  const [overtimeEndTime, setOvertimeEndTime] = useState(""); // 残業終了予定時刻（事前申請）
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [reason, setReason] = useState("");
  const [breakByApp, setBreakByApp] = useState({}); // 本申請時の休憩(分): appId -> 分
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [supervisorPending, setSupervisorPending] = useState([]);
  const [supervisorApproved, setSupervisorApproved] = useState([]);
  const [busyId, setBusyId] = useState("");

  /** 遅刻証など添付ファイル（遅刻申請のみ） */
  const [attachment, setAttachment] = useState(null);
  const [attachmentError, setAttachmentError] = useState("");

  // 時刻入力の完成を検知して次のフィールドへ自動フォーカスする処理は廃止した。
  // iOS Safari の type="time" はピッカーを開いた時点で値が常に HH:MM 形式のため、
  // ドラムを動かすたびに「完成した」と判定されてフォーカスが奪われ、
  // ピッカーが閉じて時刻を選べなくなっていた。

  const cfg = CATEGORY_CONFIG[category];

  const fetchHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const res = await api.get("/api/employee/punch-applications/");
      setHistory(Array.isArray(res.data) ? res.data : (res.data?.results || []));
    } catch (e) {
      console.error(e);
    } finally {
      setHistLoading(false);
    }
  }, []);

  const fetchSupervisorPanels = useCallback(async () => {
    try {
      const res = await api.get("/api/employee/my-overtime/supervisor-pending/");
      setSupervisorPending(Array.isArray(res.data?.pending) ? res.data.pending : []);
      setSupervisorApproved(Array.isArray(res.data?.approved) ? res.data.approved : []);
    } catch (e) {
      if (e.response?.status !== 403 && e.response?.status !== 404) {
        console.error(e);
      }
      setSupervisorPending([]);
      setSupervisorApproved([]);
    }
  }, []);

  useEffect(() => {
    if (tab === "history") fetchHistory();
  }, [tab, fetchHistory]);

  useEffect(() => {
    fetchSupervisorPanels();
  }, [fetchSupervisorPanels]);

  useEffect(() => {
    const pc = searchParams.get("punchCategory");
    if (pc && CATEGORY_CONFIG[pc]) {
      setCategory(pc);
    }
    if (pc === "LATE_IN") {
      const scheduleStart = searchParams.get("scheduleStart");
      const plannedStart = searchParams.get("plannedStart");
      if (scheduleStart) setStartTime(scheduleStart);
      if (plannedStart) setEndTime(plannedStart);
      setDate(today());
    }
  }, [searchParams]);

  // 早退申請の時刻を自動設定する。
  // 「退社予定」「退社時刻」はどちらも readOnly なので、ここで入れないと空欄のまま＝
  // 早退時間（退社予定 − 退社時刻）も表示されない。
  // ・退勤打刻から来た場合  … クエリ（scheduleEnd / plannedEnd）を使う
  // ・申請メニューから直接開いた場合 … 勤務予定を取得して退社予定を補完する
  useEffect(() => {
    if (category !== "EARLY_OUT") return;
    let alive = true;

    setDate(today());

    // 「退社時刻」（実際に退社した時刻）＝ 退勤ボタンを押した時刻。
    // scheduleEnd の有無にかかわらず必ず入れる。この欄は表示専用で社員は
    // 修正できないため、ここで入れ損ねると空欄のまま申請することになる。
    setEndTime(toHm(searchParams.get("plannedEnd")) || nowHm());

    // 「退社予定」（定時）はクエリを優先し、無ければ勤務予定から補完する。
    const scheduleEnd = toHm(searchParams.get("scheduleEnd"));
    if (scheduleEnd) {
      setStartTime(scheduleEnd);
      return undefined;
    }

    setStartTime(toHm(searchParams.get("plannedEnd")) || nowHm());
    (async () => {
      try {
        const res = await api.get("/api/worktime/status/");
        if (!alive) return;
        const se = toHm(res.data?.schedule_end);
        if (se) setStartTime(se);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { alive = false; };
  }, [category, searchParams]);

  const fromPunchLate = searchParams.get("fromPunch") === "1" && category === "LATE_IN";

  // 実残業(分) = 実際の退社 − 退社予定 − 休憩（日跨ぎ対応）
  const calcNetOvertimeMin = (scheduledEnd, actualEnd, breakMin) => {
    if (!scheduledEnd || !actualEnd) return null;
    try {
      const [sh, sm] = scheduledEnd.split(":").map(Number);
      const [eh, em] = actualEnd.split(":").map(Number);
      let gross = (eh * 60 + em) - (sh * 60 + sm);
      if (gross < 0) gross += 24 * 60; // 夜勤など日跨ぎ
      return Math.max(0, gross - (Number(breakMin) || 0));
    } catch {
      return null;
    }
  };

  const finalizeOvertime = async (appId) => {
    const breakMin = parseInt(breakByApp[appId] ?? "0", 10) || 0;
    if (!window.confirm("この残業を本申請しますか？")) return;
    setBusyId(appId);
    try {
      await api.post(`/api/employee/my-overtime/${appId}/finalize/`, { breakMinutes: breakMin });
      setMessage({ text: "本申請を送信しました。", type: "success" });
      await fetchSupervisorPanels();
      if (tab === "history") await fetchHistory();
    } catch (e) {
      setMessage({
        text: e.response?.data?.detail || "本申請に失敗しました。",
        type: "error",
      });
    } finally {
      setBusyId("");
    }
  };

  const cancelOvertime = async (appId) => {
    if (!window.confirm("この事前申請を取り下げますか？")) return;
    setBusyId(appId);
    try {
      await api.post(`/api/employee/my-overtime/${appId}/cancel/`);
      setMessage({ text: "事前申請を取り下げました。", type: "success" });
      await fetchSupervisorPanels();
    } catch (e) {
      setMessage({
        text: e.response?.data?.detail || "取り下げに失敗しました。",
        type: "error",
      });
    } finally {
      setBusyId("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ text: "", type: "" });
    if (!reason.trim()) {
      setMessage({ text: "理由を入力してください。", type: "error" });
      return;
    }
    if (category === "OVERTIME" && !overtimeEndTime) {
      setMessage({ text: "残業終了予定時刻を入力してください。", type: "error" });
      return;
    }
    if (category === "LATE_IN" && attachmentError) {
      setMessage({ text: attachmentError, type: "error" });
      return;
    }
    setLoading(true);
    try {
      // 遅刻申請で添付ファイルがある場合は multipart で送る
      const useMultipart = category === "LATE_IN" && !!attachment;
      let res;
      if (useMultipart) {
        const fd = new FormData();
        fd.append("category", category);
        fd.append("date", date);
        fd.append("reason", reason);
        fd.append("startTime", startTime);
        fd.append("endTime", endTime);
        fd.append("attachment", attachment, attachment.name);
        res = await api.post("/api/forms/submit_punch_application/", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        const body = { category, date, reason };
        if (category === "OVERTIME") {
          // 事前申請は「残業終了予定時刻」。実残業は退勤打刻時に確定する。
          body.overtimeEndTime = overtimeEndTime;
        } else {
          body.startTime = startTime;
          body.endTime = endTime;
        }
        res = await api.post("/api/forms/submit_punch_application/", body);
      }
      if (res?.data?.supervisorApprovalRequired) {
        setMessage({
          text: res.data.message || "上司の承認をリクエストしました。承認後、本申請を行ってください。",
          type: "success",
        });
      } else {
        setMessage({ text: res?.data?.message || "申請を送信しました。", type: "success" });
      }

      if (category === "LATE_IN" && searchParams.get("fromPunch") === "1") {
        try {
          window.dispatchEvent(new Event("worktime-punch-state-changed"));
        } catch (_) { /* noop */ }
        // 出社打刻は申請を送信したこの時点で行う（申請しないまま打刻だけ残るのを防ぐため、
        // 打刻処理は呼び出し元の画面に戻して実行する。GPS・在宅判定をそちらに集約しているため）。
        // latePunch には打刻ボタンを押した時刻を渡し、申請入力にかかった時間を出社時刻に含めない。
        const backTo = searchParams.get("returnTo") || "/card_front";
        const punchAt = searchParams.get("plannedStart") || "";
        navigate(punchAt ? `${backTo}?latePunch=${encodeURIComponent(punchAt)}` : backTo);
        return;
      }

      // 早退申請：申請成功時に退勤打刻（startTime = 退社時刻 が指定されていればその時刻、未指定なら現在時刻）
      let clockOutMsg = "";
      if (category === "EARLY_OUT") {
        try {
          const outBody = {};
          if (endTime) outBody.overtime_end = endTime;
          const outRes = await api.post("/api/worktime/clock_out/", outBody);
          const endStr = (outRes?.data?.end_time || "").slice(11, 16);
          clockOutMsg = endStr
            ? `退勤打刻を記録しました（${endStr}）。`
            : "退勤打刻を記録しました。";
        } catch (clockErr) {
          const m =
            clockErr.response?.data?.message ||
            clockErr.response?.data?.detail ||
            "退勤打刻には失敗しました（既に退勤済み・出勤記録なし等）。";
          clockOutMsg = m;
        }
      }
      if (clockOutMsg) {
        setMessage((prev) => ({
          text: `${prev.text}\n${clockOutMsg}`,
          type: prev.type || "success",
        }));
        try {
          window.dispatchEvent(new Event("worktime-punch-state-changed"));
        } catch (_) { /* noop */ }
      }

      setReason("");
      setOvertimeHours("");
      setBreakTime("00:00");
      setOvertimeEndTime("");
      setStartTime("");
      setEndTime("");
      setDate(today());
      setAttachment(null);
      setAttachmentError("");
      await fetchSupervisorPanels();
    } catch (e) {
      setMessage({ text: e.response?.data?.error || "送信に失敗しました。", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const overtimeMinutes = category === "OVERTIME" && overtimeHours
    ? (() => {
        try {
          const [h, m] = overtimeHours.split(":").map(Number);
          const [bh, bm] = breakTime.split(":").map(Number);
          return Math.max(0, (h * 60 + m) - (bh * 60 + bm));
        } catch { return null; }
      })()
    : null;

  const lateEarlyMinutes = (category === "LATE_IN" || category === "EARLY_OUT")
    ? calcMinutes(startTime, endTime)
    : null;

  return (
    <div className="punchapp-root">
      <div className="punchapp-header">
        <h1 className="punchapp-title">残業・早退・遅刻申請</h1>
        <button
          type="button"
          className="btn-back"
          onClick={() => navigate(-1)}
          style={{ height: 40, padding: "0 18px", borderRadius: 8, border: "none", color: "#fff", background: "#0099ff", cursor: "pointer", fontSize: 14, fontWeight: 600, whiteSpace: "nowrap" }}
        >
          戻る
        </button>
        
      </div>

      {/* タブ */}
      <div className="punchapp-tabs">
        <button type="button" className={`punchapp-tab ${tab === "apply" ? "punchapp-tab--active" : ""}`} onClick={() => setTab("apply")}>
          申請
        </button>
        <button type="button" className={`punchapp-tab ${tab === "history" ? "punchapp-tab--active" : ""}`} onClick={() => setTab("history")}>
          申請履歴
        </button>
      </div>

      {tab === "apply" && (supervisorApproved.length > 0 || supervisorPending.length > 0) && (
        <div className="punchapp-card" style={{ marginBottom: 12 }}>
          {supervisorApproved.length > 0 && (
            <div
              style={{
                background: "#ecfdf5",
                border: "1px solid #6ee7b7",
                borderRadius: 8,
                padding: "12px 14px",
                marginBottom: supervisorPending.length > 0 ? 12 : 0,
              }}
            >
              <div style={{ fontWeight: 700, color: "#065f46", marginBottom: 8 }}>
                上司承認済み — 本申請待ち
              </div>
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {supervisorApproved.map((row) => (
                  <li
                    key={row.id}
                    style={{
                      borderTop: "1px dashed #a7f3d0",
                      paddingTop: 10,
                      marginTop: 10,
                      fontSize: "0.88rem",
                      color: "#064e3b",
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>
                      {row.date}{row.plannedEnd ? ` ／ 残業終了予定 ${row.plannedEnd}` : ""}
                    </div>
                    {row.reason && (
                      <div style={{ marginTop: 4, lineHeight: 1.4 }}>{row.reason}</div>
                    )}
                    {/* 退社予定・実際の退社（打刻）・休憩入力・実残業（自動計算） */}
                    <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", fontSize: "0.85rem" }}>
                      <span>退社予定: <b>{row.scheduledEnd || "—"}</b></span>
                      <span>実際の退社(打刻): <b>{row.actualEnd || "未打刻"}</b></span>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        残業休憩
                        <select
                          value={String(breakByApp[row.id] ?? "0")}
                          onChange={(e) => setBreakByApp((prev) => ({ ...prev, [row.id]: e.target.value }))}
                          style={{ padding: "4px 6px", borderRadius: 4, border: "1px solid #6ee7b7" }}
                        >
                          {OVERTIME_BREAK_HOUR_OPTIONS.map((h) => (
                            <option key={h} value={String(h * 60)}>{String(h)}</option>
                          ))}
                        </select>
                        時間
                      </label>
                      {(() => {
                        const net = calcNetOvertimeMin(row.scheduledEnd, row.actualEnd, breakByApp[row.id] ?? 0);
                        return (
                          <span>
                            実残業: <b style={{ color: "#065f46" }}>{net == null ? "—" : formatMinutes(net)}</b>
                          </span>
                        );
                      })()}
                    </div>
                    {!row.actualEnd && (
                      <div style={{ marginTop: 6, fontSize: "0.8rem", color: "#b45309" }}>
                        ※ まだ退勤打刻がありません。退勤打刻のあとに本申請してください（実残業は打刻から計算します）。
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() => finalizeOvertime(row.id)}
                        disabled={busyId === row.id || !row.actualEnd}
                        style={{
                          padding: "8px 16px",
                          background: row.actualEnd ? "#16a34a" : "#9ca3af",
                          color: "#fff",
                          border: "none",
                          borderRadius: 6,
                          cursor: row.actualEnd ? "pointer" : "not-allowed",
                          fontWeight: 700,
                          fontSize: "0.88rem",
                        }}
                      >
                        {busyId === row.id ? "送信中..." : "本申請する"}
                      </button>
                      <button
                        type="button"
                        onClick={() => cancelOvertime(row.id)}
                        disabled={busyId === row.id}
                        style={{
                          padding: "8px 14px",
                          background: "#fff",
                          color: "#065f46",
                          border: "1px solid #6ee7b7",
                          borderRadius: 6,
                          cursor: "pointer",
                          fontWeight: 600,
                          fontSize: "0.82rem",
                        }}
                      >
                        取り下げ
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {supervisorPending.length > 0 && (
            <div
              style={{
                background: "#fffbeb",
                border: "1px solid #fcd34d",
                borderRadius: 8,
                padding: "12px 14px",
              }}
            >
              <div style={{ fontWeight: 700, color: "#92400e", marginBottom: 8 }}>
                上司承認待ち
              </div>
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {supervisorPending.map((row) => (
                  <li
                    key={row.id}
                    style={{
                      borderTop: "1px dashed #fde68a",
                      paddingTop: 10,
                      marginTop: 10,
                      fontSize: "0.88rem",
                      color: "#451a03",
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>
                      {row.date}{row.overtimeHours ? ` ／ 残業 ${row.overtimeHours}` : ""}
                    </div>
                    {row.reason && (
                      <div style={{ marginTop: 4, lineHeight: 1.4 }}>{row.reason}</div>
                    )}
                    <div style={{ marginTop: 8 }}>
                      <button
                        type="button"
                        onClick={() => cancelOvertime(row.id)}
                        disabled={busyId === row.id}
                        style={{
                          padding: "6px 12px",
                          background: "#fff",
                          color: "#92400e",
                          border: "1px solid #fcd34d",
                          borderRadius: 6,
                          cursor: "pointer",
                          fontWeight: 600,
                          fontSize: "0.82rem",
                        }}
                      >
                        {busyId === row.id ? "処理中..." : "取り下げ"}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {tab === "apply" && (
        <div className="punchapp-card">
          {/* 申請種別 */}
          <div className="punchapp-type-row">
            {Object.entries(CATEGORY_CONFIG).map(([key, c]) => (
              <button
                key={key}
                type="button"
                className={`punchapp-type-btn ${category === key ? "punchapp-type-btn--active" : ""}`}
                style={category === key ? { background: c.color, color: "#fff", borderColor: c.color } : {}}
                onClick={() => { setCategory(key); setMessage({ text: "", type: "" }); }}
              >
                {c.label}
              </button>
            ))}
          </div>
          
          {/* ヒント */}
          {/*}
          <div className="punchapp-hint" style={{ background: cfg.hintBg, color: cfg.hintColor }}>
            {cfg.hint}
          </div>
          
          {fromPunchLate && (
            <p
              className="punchapp-minutes"
              style={{ margin: "0 0 12px", color: "#0f766e", fontWeight: 600 }}
              role="status"
            >
              出社打刻済みです。遅刻理由を入力して送信すると社員証に戻ります。
            </p>
          )}
          

          {(fromPunchLate || category === "EARLY_OUT") && (
            <p
              className="punchapp-minutes"
              style={{ margin: "0 0 12px", color: "#b45309", fontWeight: 600 }}
              role="status"
            >
              {fromPunchLate
                ? "この申請を送信すると出社打刻を記録します。送信しないと出社打刻は記録されません。"
                : "この申請を送信すると退勤打刻を記録します。送信しないと退勤打刻は記録されません。"}
            </p>
          )}
          */}

          <form onSubmit={handleSubmit}>
            {/* 日付 */}
            <div className="punchapp-field">
              <label className="punchapp-label">日付<span className="punchapp-required">*</span></label>
              <input type="date" className="punchapp-input" value={date} readOnly
              //onChange={(e) => setDate(e.target.value)} required 
              />
            </div>

            {category === "OVERTIME" ? (
              <>
                <div className="punchapp-field">
                  <label className="punchapp-label">残業終了予定時刻<span className="punchapp-required">*</span></label>
                  {/* iOS Safari のドラム式ピッカーを避けるため、時・分を直接打つ入力にしている */}
                  <TimePartsInput
                    value={overtimeEndTime}
                    onChange={setOvertimeEndTime}
                    label="残業終了予定時刻"
                    required
                  />
                  <p style={{ fontSize: "0.78rem", color: "#6b7280", marginTop: 4 }}>
                    何時まで残業する予定かを入力してください。<strong>実際の残業時間は退勤打刻のあと、本申請で確定</strong>します（実退社時刻 − 退社予定 − 休憩）。
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="punchapp-time-row">
                  <div className="punchapp-field">
                    <label className="punchapp-label">{category === "LATE_IN" ? "出社時刻" : "退社時刻"}</label>
                    {/* 社員は修正しない項目なので入力欄にしない。readOnly の
                        <input type="time"> は iOS Safari では「押せるのに反応しない」
                        状態になり、入力できないという誤解を招くため文字表示にしている。 */}
                    <p className="punchapp-static">{endTime || "—"}</p>
                  </div>
                  <div className="punchapp-field">
                    <label className="punchapp-label">{category === "LATE_IN" ? "出社予定" : "退社予定"}</label>
                    <p className="punchapp-static">{startTime || "—"}</p>
                  </div>
                </div>
                {lateEarlyMinutes !== null && lateEarlyMinutes >= 0 && (
                  <p className="punchapp-minutes" style={{ color: cfg.color }}>
                    {category === "LATE_IN" ? "遅刻" : "早退"}：{formatMinutes(lateEarlyMinutes)}
                  </p>
                )}
              </>
            )}

            {/* 理由 */}
            <div className="punchapp-field">
              <label className="punchapp-label">理由<span className="punchapp-required">*</span></label>
              <textarea
                className="punchapp-textarea"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="申請理由を入力してください"
                required
              />
            </div>

            {/* 遅刻申請のみ：遅刻証・写真の添付 */}
            {category === "LATE_IN" && (
              <div className="punchapp-field">
                <label className="punchapp-label">
                  遅延証明書・写真の添付
                </label>
                <p style={{ fontSize: "0.78rem", color: "#6b7280", marginTop: 0, marginBottom: 6 }}>
                  電車遅延証明書などの写真・PDF を添付できます<br></br>（最大 5MB）。
                </p>
                <input
                  type="file"
                  accept={ACCEPT_ATTACHMENT}
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0];
                    if (!f) {
                      setAttachment(null);
                      setAttachmentError("");
                      return;
                    }
                    if (f.size > MAX_ATTACHMENT_BYTES) {
                      setAttachment(null);
                      setAttachmentError("ファイルサイズが 5MB を超えています。");
                      return;
                    }
                    setAttachment(f);
                    setAttachmentError("");
                  }}
                />
                {attachment && !attachmentError && (
                  <p style={{ fontSize: "0.8rem", color: "#065f46", marginTop: 6 }}>
                    選択中: {attachment.name}（{Math.round(attachment.size / 1024)} KB）
                  </p>
                )}
                {attachmentError && (
                  <p style={{ fontSize: "0.8rem", color: "#dc2626", marginTop: 6 }}>
                    {attachmentError}
                  </p>
                )}
              </div>
            )}

            {message.text && (
              <div className={`punchapp-message punchapp-message--${message.type}`}>{message.text}</div>
            )}

            <button
              type="submit"
              className="punchapp-submit"
              style={{ background: cfg.color }}
              disabled={loading}
            >
              {loading ? "送信中..." : `${cfg.label}を送信`}
            </button>
          </form>
        </div>
      )}

      {tab === "history" && (
        <div className="punchapp-card">
          <h2 className="punchapp-history-title">申請履歴</h2>
          {histLoading ? (
            <p className="punchapp-loading">読み込み中...</p>
          ) : history.length === 0 ? (
            <p className="punchapp-no-data">申請履歴はありません。</p>
          ) : (
            <div className="punchapp-history-list">
              {history.map((item) => {
                const st = STATUS_LABELS[item.status] || { label: item.status, bg: "#6b7280" };
                const catCfg = CATEGORY_CONFIG[item.category] || {};
                const fd = item.formData || {};
                return (
                  <div key={item.id} className="punchapp-history-item">
                    <div className="punchapp-history-top">
                      <span className="punchapp-history-category" style={{ color: catCfg.color }}>
                        {catCfg.label || item.category}
                      </span>
                      <span className="punchapp-history-status" style={{ background: st.bg }}>
                        {st.label}
                      </span>
                    </div>
                    <div className="punchapp-history-date">
                      {fd.overtime_date || fd.date || item.submitted_at?.slice(0, 10) || ""}
                    </div>
                    {(fd.overtime_hours || fd.startTime) && (
                      <div className="punchapp-history-time">
                        {item.category === "OVERTIME"
                          ? `残業: ${fd.overtime_hours || ""}`
                          : `${fd.startTime || ""}〜${fd.endTime || ""}`}
                      </div>
                    )}
                    <div className="punchapp-history-reason">{fd.reason || item.reason || item.content}</div>
                    {item.attachmentUrl && (
                      <div style={{ marginTop: 6, fontSize: "0.82rem" }}>
                        添付:{" "}
                        <a
                          href={item.attachmentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#2563eb", textDecoration: "underline" }}
                        >
                          {item.attachmentName || "ファイルを開く"}
                        </a>
                      </div>
                    )}
                    {(item.admin_comment || item.adminComment) && (
                      <div className="punchapp-history-comment">コメント: {item.admin_comment || item.adminComment}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimeOverForm;
